import { exercises, sportCategories } from '@/components/training/exerciseData';

// Classify sport into category
export const classifySport = (sport) => {
  for (const [category, sports] of Object.entries(sportCategories)) {
    if (sports.includes(sport)) return category;
  }
  return 'general';
};

// Get volume multiplier based on level
const getVolumeMultiplier = (level) => {
  switch (level) {
    case 'beginner':
      return 0.7;
    case 'intermediate':
      return 1;
    case 'advanced':
      return 1.3;
    default:
      return 1;
  }
};

// Filter exercises by available equipment
const filterByEquipment = (exerciseList, equipment) => {
  return exerciseList.filter((ex) => ex.equipment.some((eq) => equipment.includes(eq)));
};

// Pick random exercises from a list
const pickRandom = (list, count) => {
  const shuffled = [...list].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, shuffled.length));
};

// Adjust sets based on level
const adjustSetsForLevel = (exercise, level) => {
  const multiplier = getVolumeMultiplier(level);
  return {
    ...exercise,
    sets: Math.max(2, Math.round(exercise.sets * multiplier)),
  };
};

// Generate a full-body workout
const generateFullBody = (equipment, level) => {
  const workout = {
    type: 'Full Body',
    focus: 'Complete body workout',
    exercises: [],
  };

  const pushExs = filterByEquipment(exercises.push, equipment);
  const pullExs = filterByEquipment(exercises.pull, equipment);
  const legExs = filterByEquipment(exercises.legs, equipment);
  const coreExs = filterByEquipment(exercises.core, equipment);

  workout.exercises = [
    ...pickRandom(pushExs, 2),
    ...pickRandom(pullExs, 2),
    ...pickRandom(legExs, 2),
    ...pickRandom(coreExs, 1),
  ].map((ex) => adjustSetsForLevel(ex, level));

  return workout;
};

// Generate upper body workout
const generateUpperBody = (equipment, level) => {
  const workout = {
    type: 'Upper Body',
    focus: 'Push & Pull',
    exercises: [],
  };

  const pushExs = filterByEquipment(exercises.push, equipment);
  const pullExs = filterByEquipment(exercises.pull, equipment);
  const coreExs = filterByEquipment(exercises.core, equipment);

  workout.exercises = [
    ...pickRandom(pushExs, 3),
    ...pickRandom(pullExs, 3),
    ...pickRandom(coreExs, 1),
  ].map((ex) => adjustSetsForLevel(ex, level));

  return workout;
};

// Generate lower body workout
const generateLowerBody = (equipment, level) => {
  const workout = {
    type: 'Lower Body',
    focus: 'Legs & Core',
    exercises: [],
  };

  const legExs = filterByEquipment(exercises.legs, equipment);
  const coreExs = filterByEquipment(exercises.core, equipment);

  workout.exercises = [...pickRandom(legExs, 5), ...pickRandom(coreExs, 2)].map((ex) =>
    adjustSetsForLevel(ex, level),
  );

  return workout;
};

// Generate sport-specific workout
const generateSportSpecific = (sport, equipment, level) => {
  const drills = exercises.sportDrills[sport] || exercises.sportDrills.general;
  const availableDrills = filterByEquipment(drills, equipment);

  const workout = {
    type: 'Sport Specific',
    focus: `${sport.charAt(0).toUpperCase() + sport.slice(1)} Training`,
    exercises: pickRandom(availableDrills, 4).map((ex) => adjustSetsForLevel(ex, level)),
  };

  return workout;
};

// Generate cardio/endurance workout
const generateCardio = (equipment, level) => {
  const workout = {
    type: 'Cardio',
    focus: 'Endurance & Conditioning',
    exercises: [],
  };

  const cardioExs = filterByEquipment(exercises.cardio, equipment);

  workout.exercises = pickRandom(cardioExs, 3).map((ex) => adjustSetsForLevel(ex, level));

  return workout;
};

// Main routine generator
export const generateRoutine = ({ sport, days, level, equipment }) => {
  const category = classifySport(sport);
  const routine = [];
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Distribute workout days evenly across the week
  const daySpacing = Math.floor(7 / days);
  const workoutDays = [];
  for (let i = 0; i < days; i++) {
    workoutDays.push((i * daySpacing) % 7);
  }
  workoutDays.sort((a, b) => a - b);

  // Generate workouts based on days available and sport category
  if (days <= 2) {
    // Low frequency: Full body + Sport specific
    workoutDays.forEach((dayIndex, i) => {
      routine.push({
        day: dayNames[dayIndex],
        ...(i % 2 === 0
          ? generateFullBody(equipment, level)
          : generateSportSpecific(sport, equipment, level)),
      });
    });
  } else if (days <= 4) {
    // Medium frequency: Alternate strength and sport-specific
    workoutDays.forEach((dayIndex, i) => {
      let workout;
      switch (i % 4) {
        case 0:
          workout = generateUpperBody(equipment, level);
          break;
        case 1:
          workout = generateSportSpecific(sport, equipment, level);
          break;
        case 2:
          workout = generateLowerBody(equipment, level);
          break;
        case 3:
          workout =
            category === 'endurance'
              ? generateCardio(equipment, level)
              : generateSportSpecific(sport, equipment, level);
          break;
        default:
          workout = generateFullBody(equipment, level);
      }
      routine.push({ day: dayNames[dayIndex], ...workout });
    });
  } else {
    // High frequency: More variety
    workoutDays.forEach((dayIndex, i) => {
      let workout;
      switch (i % 6) {
        case 0:
          workout = generateUpperBody(equipment, level);
          break;
        case 1:
          workout = generateSportSpecific(sport, equipment, level);
          break;
        case 2:
          workout = generateLowerBody(equipment, level);
          break;
        case 3:
          workout =
            category === 'endurance'
              ? generateCardio(equipment, level)
              : generateFullBody(equipment, level);
          break;
        case 4:
          workout = generateSportSpecific(sport, equipment, level);
          break;
        case 5:
          workout = generateCardio(equipment, level);
          break;
        default:
          workout = generateFullBody(equipment, level);
      }
      routine.push({ day: dayNames[dayIndex], ...workout });
    });
  }

  return {
    sport,
    category,
    level,
    daysPerWeek: days,
    equipment,
    routine,
    generatedAt: new Date().toISOString(),
  };
};
